import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cantieri as cantieriApi, supporto as supportoApi, billing as billingApi, notifiche as notificheApi } from '../utils/api';
import ChatWidget from './ChatWidget';
import ImpersonationBanner from './ImpersonationBanner';
import Breadcrumb from './shared/Breadcrumb';
import FavoritesBar from './shared/FavoritesBar';
import { SIDEBAR, activeSidebarKey } from '../config/navigation';
import {
  HardHat, Settings, Plus, ChevronDown, HardDrive,
  Bell, Mail, MessageSquare, Menu, LogOut,
} from 'lucide-react';

const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// ── Sidebar a 6 macro-voci FISSE (Fase 1 restyling) ───────────────
// REGOLA D'ORO: cambia solo l'aspetto e la struttura di navigazione.
// Le route restano identiche. La tassonomia vive in config/navigation.js
// (fonte unica: sidebar + Hub + breadcrumb leggono da lì).

// Voce di sidebar: apre una pagina diretta (Home, Controllo Gestionale) oppure
// una Hub. Attiva anche quando si è su una sua sotto-pagina (es. /cantieri
// evidenzia "Operatività").
function MacroItem({ macro, active, badge, onClick }) {
  const Icon = macro.icon;
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      title={macro.label}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderRadius: 'var(--radius-base)', border: 'none', background: active ? 'var(--chip)' : 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'background .12s' }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--chip)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon size={18} strokeWidth={1.8} color={macro.color} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 13, flex: 1, fontWeight: active ? 500 : 400, color: active ? macro.color : 'var(--ds-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{macro.label}</span>
      {badge}
    </button>
  );
}

function Avatar({ nome, cognome, size = 7 }) {
  const initials = `${nome?.[0] || ''}${cognome?.[0] || ''}`.toUpperCase() || '?';
  const px = size * 4; // scala coerente con le dimensioni precedenti (7→28, 10→40)
  return (
    <div style={{
      width: px, height: px, borderRadius: '50%', background: 'var(--brand)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 500, color: 'white', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function tempoRelativo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 0) return d.toLocaleDateString('it-IT');
  if (diff < 60) return 'adesso';
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  const g = Math.floor(diff / 86400);
  if (g === 1) return 'ieri';
  if (g < 30) return `${g} g fa`;
  return d.toLocaleDateString('it-IT');
}

function NotifMenu({ Icon, titolo, data, readSet, isOpen, onToggle, onItemClick, onMarkAll, emptyLabel, badgeColor = 'var(--ds-danger)' }) {
  const [hover, setHover] = useState(false);
  const items = data?.items || [];
  // Badge = item ricevuti NON ancora letti (localStorage). Niente overflow
  // fantasma: il backend restituisce (quasi) tutti gli item, quindi ogni
  // item conteggiato e' anche azzerabile con click / "Segna tutte lette".
  const nonLette = items.filter((it) => !readSet.has(it.id)).length;
  const active = hover || isOpen;
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={onToggle}
        aria-label={titolo}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ position: 'relative', width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', margin: '0 2px', background: active ? 'var(--chip)' : 'transparent', border: 'none', transition: 'background .12s' }}
      >
        <Icon size={18} strokeWidth={1.8} color={active ? 'var(--ink)' : 'var(--ds-muted)'} />
        {nonLette > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2,
            minWidth: 15, height: 15, padding: '0 3px', borderRadius: 8,
            background: badgeColor, border: '1.5px solid var(--ds-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, fontWeight: 500, color: 'white', boxSizing: 'border-box',
          }}>
            {nonLette > 9 ? '9+' : nonLette}
          </span>
        )}
      </button>
      {isOpen && (
        <div style={{
          position: 'absolute', top: 40, right: 0, width: 320, maxWidth: 'calc(100vw - 24px)', maxHeight: 400, overflowY: 'auto',
          background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)',
          boxShadow: '0 8px 28px rgba(35,38,47,0.16)', zIndex: 40, padding: 6,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px 8px' }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{titolo}</span>
            {nonLette > 0 && (
              <button
                onClick={() => onMarkAll(items)}
                style={{ border: 'none', background: 'transparent', color: 'var(--brand)', fontSize: 12, cursor: 'pointer', padding: 0 }}
              >
                Segna tutte lette
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: '20px 14px', textAlign: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>{emptyLabel}</div>
          ) : (
            items.map((it) => {
              const letta = readSet.has(it.id);
              const quando = tempoRelativo(it.quando);
              return (
                <button
                  key={it.id}
                  onClick={() => onItemClick(it.link, it.id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 'var(--radius-base)', opacity: letta ? 0.55 : 1 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--chip)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: letta ? 400 : 500, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.titolo}</span>
                    {quando ? <span style={{ flexShrink: 0, fontSize: 11, color: 'var(--ds-muted)' }}>{quando}</span> : null}
                  </div>
                  {it.sottotitolo ? (
                    <div style={{ fontSize: 12, color: 'var(--ds-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.sottotitolo}</div>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, piano, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentKey = activeSidebarKey(location.pathname);
  const [cantieriCount, setCantieriCount] = useState(null);
  const [ticketAperti, setTicketAperti] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [billingInfo, setBillingInfo] = useState(null);
  const userMenuRef = useRef(null);

  // ── Centro notifiche (aggregate-on-read) — 3 icone topbar ──
  const [openNotif, setOpenNotif] = useState(null); // 'operative' | 'comunicazioni' | 'conversazioni' | null
  const [notif, setNotif] = useState({
    operative: { count: 0, items: [] },
    comunicazioni: { count: 0, items: [] },
    conversazioni: { count: 0, items: [] },
  });
  // Stato "letto" per singola notifica (localStorage, per-dispositivo).
  const [notifLette, setNotifLette] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('notifiche_lette') || '[]')); } catch { return new Set(); }
  });
  const notifRef = useRef(null);

  const persistLette = (set) => {
    try { localStorage.setItem('notifiche_lette', JSON.stringify([...set])); } catch { /* noop */ }
  };
  const marcaLetta = useCallback((id) => {
    setNotifLette((prev) => { const next = new Set(prev); next.add(id); persistLette(next); return next; });
  }, []);
  const marcaTutteLette = useCallback((items) => {
    setNotifLette((prev) => { const next = new Set(prev); items.forEach((it) => next.add(it.id)); persistLette(next); return next; });
  }, []);

  // ── Mobile responsive (Step 6 G2) — drawer off-canvas sotto i 768px ──
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => { setIsMobile(e.matches); if (!e.matches) setMobileNavOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  const fetchUnread = useCallback(() => {
    supportoApi.unreadCount()
      .then(r => setTicketAperti(r.data?.count ?? 0))
      .catch(() => {});
  }, []);

  const fetchNotifiche = useCallback(() => {
    notificheApi.list()
      .then(r => {
        if (!r.data) return;
        setNotif(r.data);
        // Igiene localStorage: tieni solo gli id ancora presenti nel fetch
        // (una notifica risolta sparisce -> non serve ricordarla come letta).
        const vivi = new Set();
        ['operative', 'comunicazioni', 'conversazioni'].forEach((g) => {
          (r.data[g]?.items || []).forEach((it) => vivi.add(it.id));
        });
        setNotifLette((prev) => {
          const next = new Set([...prev].filter((id) => vivi.has(id)));
          if (next.size !== prev.size) persistLette(next);
          return next;
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    cantieriApi.list({ page_size: 1 })
      .then(r => setCantieriCount(r.data?.count ?? null))
      .catch(() => {});
    billingApi.me()
      .then(r => setBillingInfo(r.data))
      .catch(() => {});
    fetchUnread();
    fetchNotifiche();
    // Polling ogni 60s + al focus finestra
    const interval = setInterval(() => { fetchUnread(); fetchNotifiche(); }, 60000);
    const onFocus = () => { fetchUnread(); fetchNotifiche(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [fetchUnread, fetchNotifiche]);

  // Chiusura dropdown notifiche al click fuori
  useEffect(() => {
    if (!openNotif) return;
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setOpenNotif(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openNotif]);

  const handleNotifClick = (link, id) => {
    if (id) marcaLetta(id);
    setOpenNotif(null);
    if (link) navigate(link);
  };

  // Apri/chiudi un gruppo. Il badge NON si azzera al semplice sguardo: cala
  // solo quando marchi esplicitamente (click su un item o "Segna tutte lette").
  const toggleNotif = (gruppo) => setOpenNotif((o) => (o === gruppo ? null : gruppo));

  const handleLogout = () => { logout(); navigate('/auth/login'); };

  // ── Dati card account (reali da /billing/me/ + lista cantieri) ──
  const aziendaNome = user?.tenant?.nome || 'La tua azienda';
  const aziendaInitials = aziendaNome.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'M';
  const limiteCantieri = billingInfo?.limiti?.cantieri ?? 0;          // 0 = illimitato
  const storageUso = billingInfo?.utilizzi?.storage_gb ?? 0;
  const storageLimite = billingInfo?.limiti?.storage_gb ?? 0;
  const cantieriPct = (limiteCantieri > 0 && cantieriCount != null)
    ? Math.min(100, Math.round((cantieriCount / limiteCantieri) * 100))
    : (cantieriCount != null ? Math.min(100, Math.round((cantieriCount / 20) * 100)) : 0);
  const storagePct = storageLimite > 0 ? Math.min(100, Math.round((storageUso / storageLimite) * 100)) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--ds-bg)' }}>

      {/* Restyling Step 0 — barra accent brand full-width in cima */}
      <div style={{ height: 3, width: '100%', background: 'var(--brand-gradient)', flexShrink: 0 }} />

      {/* SEC-002 — banner sessione impersonata (visibile su tutte le pagine) */}
      <ImpersonationBanner />

      {/* ── TOPBAR ── */}
      <header style={{
        height: 56, background: 'var(--ds-bg)', borderBottom: '1px solid var(--ds-border)',
        display: 'flex', alignItems: 'center', padding: '0 20px', gap: 16, flexShrink: 0, zIndex: 20,
        boxShadow: '0 2px 10px rgba(35,38,47,0.06)'
      }}>
        {/* Hamburger — solo mobile: apre il drawer della sidebar */}
        {isMobile && (
          <button
            onClick={() => setMobileNavOpen(true)}
            aria-label="Apri menu"
            style={{ width: 40, height: 40, borderRadius: 'var(--radius-base)', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: -8 }}
          >
            <Menu size={22} strokeWidth={1.8} color="var(--ds-text)" />
          </button>
        )}
        {/* Breadcrumb "Home / Macro / Sotto" — derivato dalla route (config navigation.js) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Breadcrumb />
        </div>

        {/* Notification icons — centro notifiche aggregate-on-read */}
        <div ref={notifRef} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <NotifMenu
            Icon={Bell} titolo="Attività" data={notif.operative} readSet={notifLette}
            isOpen={openNotif === 'operative'}
            onToggle={() => toggleNotif('operative')}
            onItemClick={handleNotifClick} onMarkAll={marcaTutteLette}
            emptyLabel="Nessuna attività in sospeso"
          />
          <NotifMenu
            Icon={Mail} titolo="Comunicazioni" data={notif.comunicazioni} readSet={notifLette}
            isOpen={openNotif === 'comunicazioni'}
            onToggle={() => toggleNotif('comunicazioni')}
            onItemClick={handleNotifClick} onMarkAll={marcaTutteLette}
            emptyLabel="Nessuna comunicazione"
          />
          <NotifMenu
            Icon={MessageSquare} titolo="Messaggi" data={notif.conversazioni} readSet={notifLette}
            isOpen={openNotif === 'conversazioni'}
            onToggle={() => toggleNotif('conversazioni')}
            onItemClick={handleNotifClick} onMarkAll={marcaTutteLette}
            emptyLabel="Nessun messaggio in sospeso"
          />
        </div>

        {/* User menu */}
        <div ref={userMenuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserMenu(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px 4px 4px', borderRadius: 'var(--radius-base)', border: 'none', background: 'transparent', cursor: 'pointer' }}
          >
            <Avatar nome={user?.nome} cognome={user?.cognome} />
            <span style={{ fontSize: 12, color: 'var(--ink)', fontWeight: 500 }}>
              {user?.nome} {user?.cognome}
            </span>
            <ChevronDown size={12} strokeWidth={2.5} color="var(--ds-muted)" style={{ transition: 'transform .15s', transform: showUserMenu ? 'rotate(180deg)' : 'none' }} />
          </button>

          {showUserMenu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 200,
              background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', boxShadow: '0 8px 32px rgba(35,38,47,0.14)',
              border: '1px solid var(--ds-border)', minWidth: 220, overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar nome={user?.nome} cognome={user?.cognome} size={10} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{user?.nome} {user?.cognome}</div>
                  <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 2 }}>{user?.email}</div>
                </div>
              </div>

              {/* Items */}
              <div style={{ padding: '6px 0' }}>
                {[
                  { label: 'Impostazioni', icon: Settings, to: '/impostazioni' },
                  { label: 'Chatta con l\'assistenza', icon: MessageSquare, to: '/supporto' },
                ].map(item => {
                  const ItemIcon = item.icon;
                  return (
                  <button key={item.label}
                    onClick={() => { setShowUserMenu(false); if (item.to) navigate(item.to); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--ds-text)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--chip)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <ItemIcon size={15} strokeWidth={1.8} color="var(--ds-muted)" style={{ flexShrink: 0 }} />
                    {item.label}
                  </button>
                  );
                })}
              </div>

              {/* Logout */}
              <div style={{ borderTop: '1px solid var(--ds-border)', padding: '6px 0' }}>
                <button
                  onClick={() => { setShowUserMenu(false); handleLogout(); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--ds-danger)', fontWeight: 500 }}
                  onMouseEnter={e => e.currentTarget.style.background = tint('var(--ds-danger)', 6)}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <LogOut size={15} strokeWidth={1.8} style={{ flexShrink: 0 }} />
                  Esci
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ── BODY ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>

        {/* Backdrop drawer mobile — chiusura al tap fuori */}
        {isMobile && mobileNavOpen && (
          <div
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
            style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.45)', zIndex: 250 }}
          />
        )}

        {/* ── SIDEBAR (Restyling Step 0 — zone · Step 6 drawer mobile) ── */}
        <aside style={{
          width: 228, minWidth: 228, background: 'var(--ds-bg)',
          borderRight: '1px solid var(--ds-border)', display: 'flex', flexDirection: 'column',
          flexShrink: 0, overflowY: 'auto',
          ...(isMobile ? {
            position: 'fixed', top: 0, left: 0, height: '100dvh', zIndex: 300,
            transform: mobileNavOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform .25s ease',
            boxShadow: mobileNavOpen ? '0 0 40px rgba(16,24,40,0.25)' : 'none',
          } : {}),
        }}>

          {/* Logo azienda */}
          <div style={{ padding: '14px 12px 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--logo-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span className="font-display" style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{aziendaInitials}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="font-display" style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {aziendaNome}
              </div>
              <span style={{ display: 'inline-block', marginTop: 3, fontSize: 9, padding: '1px 7px', borderRadius: 999, fontWeight: 500, color: 'white', background: 'var(--brand)', textTransform: 'capitalize' }}>
                {piano}
              </span>
            </div>
          </div>

          {/* Navigazione: 7 voci fisse (2 dirette + 5 hub) — config navigation.js */}
          <nav style={{ padding: '2px 12px 0', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Etichetta menu principale */}
            <div className="font-display" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ds-muted)', fontWeight: 500, padding: '14px 10px 4px' }}>
              Menu principale
            </div>

            {SIDEBAR.map((entry) => {
              let badge = null;
              // Segnale reale già disponibile: ticket aperti → sulla voce "Servizi" (contiene Supporto).
              if (entry.key === 'servizi' && ticketAperti > 0) {
                badge = <span style={{ fontSize: 9.5, fontWeight: 500, padding: '1px 6px', borderRadius: 99, minWidth: 18, textAlign: 'center', background: tint('var(--zone-blue)', 14), color: 'var(--zone-blue)' }}>{ticketAperti}</span>;
              }
              const dest = entry.to || entry.path; // voce diretta (to) o hub (path)
              return (
                <MacroItem
                  key={entry.key}
                  macro={entry}
                  active={currentKey === entry.key}
                  badge={badge}
                  onClick={() => { if (isMobile) setMobileNavOpen(false); navigate(dest); }}
                />
              );
            })}
          </nav>

          <div style={{ flex: 1, minHeight: 12 }} />

          {/* Nuovo cantiere CTA */}
          <div style={{ padding: '8px 12px 8px' }}>
            <button
              data-testid="sidebar-nuovo-cantiere"
              onClick={() => { if (isMobile) setMobileNavOpen(false); navigate('/cantieri/nuovo'); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 'var(--radius-base)', padding: '10px 0', fontSize: 12.5, fontWeight: 500, color: 'white', background: 'var(--brand)', border: 'none', cursor: 'pointer', boxShadow: '0 2px 10px rgba(46,160,132,0.18)' }}
            >
              <Plus size={15} strokeWidth={2.5} />
              Nuovo Cantiere
            </button>
          </div>

          {/* Card account (unica) — dati reali da /billing/me/ */}
          <div style={{ margin: '0 12px 16px', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', padding: 12, background: 'var(--chip)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
              <span className="font-display" style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {aziendaNome}
              </span>
              <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 999, fontWeight: 500, color: 'white', background: 'var(--brand)', textTransform: 'capitalize', flexShrink: 0 }}>
                {piano}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Cantieri attivi */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 10, color: 'var(--ds-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <HardHat size={11} color="var(--ds-muted)" /> Cantieri attivi
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--ds-text)' }}>
                    {cantieriCount ?? '…'}{limiteCantieri > 0 ? ` / ${limiteCantieri}` : ''}
                  </span>
                </div>
                <div style={{ height: 3, borderRadius: 999, background: 'var(--ds-border)' }}>
                  <div style={{ width: `${cantieriPct}%`, height: '100%', borderRadius: 999, background: 'var(--brand)' }} />
                </div>
              </div>

              {/* Storage usato */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 10, color: 'var(--ds-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <HardDrive size={11} color="var(--ds-muted)" /> Storage usato
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 500, color: 'var(--ds-text)' }}>
                    {billingInfo ? `${storageUso} / ${storageLimite} GB` : '…'}
                  </span>
                </div>
                <div style={{ height: 3, borderRadius: 999, background: 'var(--ds-border)' }}>
                  <div style={{ width: `${storagePct}%`, height: '100%', borderRadius: 999, background: 'var(--brand)' }} />
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* ── MAIN ── */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Barra Preferiti persistente — sempre in testa alla pagina aperta */}
          <FavoritesBar />
          {children}
        </main>
      </div>
      <ChatWidget />
    </div>
  );
}
